import { useTranslation } from "react-i18next";
import { parameterName } from "../../../../parts/other-exercise/CustomParametersSets";
import SvgIcon from "../../../../parts/SvgIcon";
import { useRillContext } from "../../../../rill/RillReact/share/base";
import { getAllSetsCounts, shouldHaveE1RM } from "../../../../utils/actuals";
import { isPROutcome } from "../../../../utils/personal-records";
import AthleteExerciseNote from "../../program-builder/components/AthleteExerciseNote";

const {
  PARAMETER_OPTIONS: { FREEFORM, ROUNDS, SETS },
} = require("../../../../constants");

const StatusComponent = ({ total, completed, missed }) => {
  if (completed === 0 && missed === 0) {
    return (
      <div className="status-missed">
        <SvgIcon src="/img/new-icons/info-circle.svg" noFilterOnLight={true} />
      </div>
    );
  }

  if (completed > 0) {
    return (
      <>
        <div className="status-completed">
          <SvgIcon
            src="/img/new-icons/tick-square.svg"
            noFilterOnLight={true}
          />
          {`${completed}/${total}`}
        </div>
        {missed > 0 && (
          <div className="status-missed">
            <SvgIcon
              src="/img/new-icons/info-circle.svg"
              noFilterOnLight={true}
            />
            {missed}
          </div>
        )}
      </>
    );
  }

  return (
    <div className="status-missed">
      <SvgIcon src="/img/new-icons/info-circle.svg" noFilterOnLight={true} />
      {`${missed}/${total}`}
    </div>
  );
};

const ActualResultsHead = (props) => {
  const { t } = useTranslation("dashboard");
  const { locals } = useRillContext();

  const { athlete, query } = locals;
  const { date, view, organizationCoach } = query;
  const {
    exercise,
    exerciseName,
    prefix,
    intensityMeasure,
    isRiR,
    hideVelocitySettings,
  } = props;
  const { parameters, type, primary, secondary, complex } = exercise;

  const showE1RM = shouldHaveE1RM(exercise);
  const showPercentRM = type === "primary" || type === "secondary";
  const { total, completed, missed } = getAllSetsCounts(exercise);

  const repMaxPR = locals.dayPersonalRecords?.repMaxPR;
  const exerciseHasPR =
    !!repMaxPR &&
    exercise.sets?.some((s) =>
      Object.values(s.outcomes || {}).some((o) =>
        isPROutcome(exercise, o, repMaxPR)
      )
    );

  let exerciseHistoryLink = query.athlete
    ? `/athlete-portal/metrics/exercise-history?type=${type}&primary=${primary}`
    : `/dashboard/metrics/exercise-history?type=${type}&primary=${primary}`;

  if (secondary && type !== "primary")
    exerciseHistoryLink += `&secondary=${secondary}`;

  if (complex) exerciseHistoryLink += `&complex=${complex}`;

  if (athlete) exerciseHistoryLink += `&athlete=${athlete._id}`;

  if (view === "day" && date) exerciseHistoryLink += `&date=${date}&view=day`;

  if (organizationCoach)
    exerciseHistoryLink += `&organizationCoach=${organizationCoach}`;

  return (
    <>
      <div className="name-wrapper justify-between">
        <div className="flex items-center gap-2">
          <span className="calendar-prefix">{prefix}</span>
          <p className="font-semibold">
            {exerciseName.charAt(0).toUpperCase() + exerciseName.slice(1)}
          </p>
        </div>

        <div className="actuals-head-status mobile-hidden">
          <span>{t("calendar.actuals.status")}</span>
          <StatusComponent
            total={total}
            completed={completed}
            missed={missed}
          />
          {view === "day" && (
            <a
              href={exerciseHistoryLink}
              data-button-type="outline"
              role="button"
            >
              <span>{t("calendar.actuals.exerciseHistory")}</span>
            </a>
          )}
        </div>
      </div>

      <AthleteExerciseNote note={exercise.athleteNotes} />

      <div className="actuals-head">
        <p className="p4 min-w-[250px] mobile-hidden">
          {t("calendar.actuals.targets")}
        </p>

        <div className="actuals-titles">
          <p className="p4">{t("calendar.actuals.results")}</p>
          <p className="p4">{t("calendar.actuals.setNumber")}</p>
          {parameters ? (
            Array.from({ length: parameters.length }, (_x, i) =>
              [FREEFORM, ROUNDS, SETS].includes(parameters[i]) ? null : (
                <p
                  key={`${i}-${exercise._id}-title`}
                  className="p4 mobile-hidden"
                >
                  {parameterName(parameters[i], isRiR)}
                </p>
              )
            )
          ) : (
            <>
              <p className="p4">{t("calendar.actuals.reps")}</p>
              <p className="p4">{intensityMeasure}</p>
              <p className="p4">{t("calendar.actuals.load")}</p>
            </>
          )}
          {showPercentRM && (
            <p className="p4 mobile-hidden">
              {t("calendar.actuals.percent1rm")}
            </p>
          )}
          {showE1RM && (
            <p className="p4 mobile-hidden">{t("calendar.actuals.e1rm")}</p>
          )}
          {exerciseHasPR && <p className="p4 pr-badge-cell mobile-hidden"></p>}
          {query?.relTrends !== false && (
            <p className="p4 trend-cell mobile-hidden">
              {t("calendar.actuals.trends")}
            </p>
          )}

          {showPercentRM && !hideVelocitySettings && (
            <p className="p4mobile-hidden">{t("calendar.actuals.velocity")}</p>
          )}
          {parameters && parameters[0] === FREEFORM && (
            <p className="p4 freeform mobile-hidden"></p>
          )}
          <p className="p4 video">{t("calendar.actuals.video")}</p>
          <p className="p4 description mobile-hidden">
            {t("calendar.actuals.comment")}
          </p>
        </div>
      </div>
    </>
  );
};

export default ActualResultsHead;
